import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';

/** FRONT-2598: the two artifact kinds the Canvas can edit by hand (documents have their own editor). */
export type EditableArtifactKind = 'sketch' | 'deck';

/**
 * `GET /api/artifacts/:kind/:id` (src/artifacts/routes.ts). `body` is the sketch's
 * HTML or a deck's structure JSON (a string). `editable` is true only for the
 * owner of a sketch, or of a deck whose structure still describes its HTML;
 * `editBlockedReason` is set only for a deck the owner cannot edit. `maxSlides` is the
 * server's own slide ceiling and is sent with every deck, editable or not.
 */
export interface ArtifactDetail {
  kind: string;
  id: string;
  title: string;
  updatedAt: string;
  body: string;
  previousVersionAvailable: boolean;
  editable: boolean;
  editBlockedReason?: string;
  maxSlides?: number;
}

export const artifactKeys = {
  all: ['artifacts'] as const,
  details: () => [...artifactKeys.all, 'detail'] as const,
  detail: (kind: EditableArtifactKind, id: string) => [...artifactKeys.details(), kind, id] as const,
};

export const artifactDetailPath = (kind: EditableArtifactKind, id: string) =>
  `/api/artifacts/${kind}/${encodeURIComponent(id)}`;

/** Only a sketch or a deck is asked about; a document or a Markdown file never causes this request. */
export function artifactDetailEnabled(kind: string | null | undefined): kind is EditableArtifactKind {
  return kind === 'sketch' || kind === 'deck';
}

export function useArtifactDetail(kind: EditableArtifactKind | null, id: string | null) {
  return useQuery({
    queryKey: artifactKeys.detail(kind ?? 'sketch', id ?? ''),
    queryFn: () => api.get<ArtifactDetail>(artifactDetailPath(kind!, id!)),
    enabled: kind !== null && !!id,
  });
}
